/**
 * ESCHER'S SACRED GEOMETRY DREAMSPACE
 * Massive impossible geometries, spirals, and sacred forms
 */

import * as THREE from 'three';

/**
 * Create MASSIVE Penrose tiling - 100x100 grid, huge tiles
 */
export const createPenroseBackground = () => {
  const group = new THREE.Group();
  
  const tileSize = 50; // MASSIVE tiles
  const gridSize = 100; // HUGE grid
  
  // Thin rhombus
  const createThinRhombus = () => {
    const shape = new THREE.Shape();
    const angle = Math.PI / 5; // 36 degrees
    shape.moveTo(0, 0);
    shape.lineTo(tileSize * Math.cos(angle), tileSize * Math.sin(angle));
    shape.lineTo(tileSize * (Math.cos(angle) + Math.cos(angle)), tileSize * Math.sin(angle));
    shape.lineTo(tileSize * Math.cos(angle), -tileSize * Math.sin(angle));
    shape.closePath();
    return new THREE.ShapeGeometry(shape);
  };
  
  // Fat rhombus
  const createFatRhombus = () => {
    const shape = new THREE.Shape();
    const angle = 2 * Math.PI / 5; // 72 degrees
    shape.moveTo(0, 0);
    shape.lineTo(tileSize * Math.cos(angle), tileSize * Math.sin(angle));
    shape.lineTo(tileSize * (Math.cos(angle) + Math.cos(angle)), tileSize * Math.sin(angle));
    shape.lineTo(tileSize * Math.cos(angle), -tileSize * Math.sin(angle));
    shape.closePath();
    return new THREE.ShapeGeometry(shape);
  };
  
  const thinGeo = createThinRhombus();
  const fatGeo = createFatRhombus();
  
  const cyanMat = new THREE.MeshBasicMaterial({ 
    color: 0x00ffff, 
    transparent: true, 
    opacity: 0.12,
    side: THREE.DoubleSide 
  });
  
  const violetMat = new THREE.MeshBasicMaterial({ 
    color: 0x8b00ff, 
    transparent: true, 
    opacity: 0.12,
    side: THREE.DoubleSide 
  });
  
  // Create MASSIVE grid
  for (let i = 0; i < gridSize; i++) {
    for (let j = 0; j < gridSize; j++) {
      if (Math.random() > 0.7) continue; // Sparse for performance
      
      const useThin = (i + j) % 2 === 0;
      const geo = useThin ? thinGeo : fatGeo;
      const mat = useThin ? cyanMat : violetMat;
      
      const mesh = new THREE.Mesh(geo, mat);
      mesh.position.set(
        (i - gridSize / 2) * tileSize * 1.2,
        (j - gridSize / 2) * tileSize * 1.2,
        -1200
      );
      mesh.rotation.z = (i * j) % 5 * (Math.PI / 5);
      group.add(mesh);
    }
  }
  
  return group;
};

/**
 * Create MASSIVE Escher staircase - circular impossible geometry
 */
export const createEscherStaircase = () => {
  const group = new THREE.Group();
  
  const steps = 24; // More steps
  const radius = 120; // MUCH BIGGER
  const stepHeight = 15;
  const stepWidth = 25;
  const stepDepth = 25;
  
  for (let i = 0; i < steps; i++) {
    const angle = (i / steps) * Math.PI * 2;
    
    // Step geometry
    const geometry = new THREE.BoxGeometry(stepWidth, stepHeight, stepDepth);
    const material = new THREE.MeshPhongMaterial({
      color: 0x00ffff,
      emissive: 0x00ffff,
      emissiveIntensity: 0.3,
      transparent: true,
      opacity: 0.6
    });
    
    const step = new THREE.Mesh(geometry, material);
    
    step.position.set(
      Math.cos(angle) * radius,
      Math.sin(angle) * radius,
      i * stepHeight - (steps * stepHeight) / 2
    );
    
    step.rotation.y = angle + Math.PI / 2;
    
    // Edge highlight
    const edges = new THREE.EdgesGeometry(geometry);
    const lineMat = new THREE.LineBasicMaterial({ color: 0x8b00ff, linewidth: 2 });
    const wireframe = new THREE.LineSegments(edges, lineMat);
    step.add(wireframe);
    
    group.add(step);
  }
  
  return group;
};

/**
 * Create MASSIVE impossible triangle - Penrose triangle
 */
export const createImpossibleTriangle = () => {
  const group = new THREE.Group();
  
  const barLength = 150; // MUCH BIGGER
  const barThickness = 20;
  
  const geometry = new THREE.BoxGeometry(barLength, barThickness, barThickness);
  const material = new THREE.MeshPhongMaterial({
    color: 0x8b00ff,
    emissive: 0x8b00ff,
    emissiveIntensity: 0.4,
    transparent: true,
    opacity: 0.7
  });
  
  // Three bars forming impossible triangle
  for (let i = 0; i < 3; i++) {
    const bar = new THREE.Mesh(geometry, material);
    
    const angle = (i / 3) * Math.PI * 2;
    const offset = barLength / 2;
    
    bar.position.set(
      Math.cos(angle) * offset,
      Math.sin(angle) * offset,
      i * barThickness
    );
    
    bar.rotation.z = angle;
    
    group.add(bar);
  }
  
  return group;
};

/**
 * Create MASSIVE 4D hypercube projection (tesseract)
 */
export const createHypercube = () => {
  const group = new THREE.Group();
  
  const size = 100; // MUCH BIGGER
  
  // 8 vertices of inner cube
  const innerVertices = [
    [-1, -1, -1], [1, -1, -1], [1, 1, -1], [-1, 1, -1],
    [-1, -1, 1], [1, -1, 1], [1, 1, 1], [-1, 1, 1]
  ].map(v => v.map(x => x * size * 0.5));
  
  // 8 vertices of outer cube
  const outerVertices = [
    [-1, -1, -1], [1, -1, -1], [1, 1, -1], [-1, 1, -1],
    [-1, -1, 1], [1, -1, 1], [1, 1, 1], [-1, 1, 1]
  ].map(v => v.map(x => x * size));
  
  const material = new THREE.LineBasicMaterial({ 
    color: 0x39ff14, 
    linewidth: 3,
    transparent: true,
    opacity: 0.8
  });
  
  // Draw inner cube edges
  const drawCube = (vertices, mat) => {
    const edges = [
      [0,1],[1,2],[2,3],[3,0], // bottom
      [4,5],[5,6],[6,7],[7,4], // top
      [0,4],[1,5],[2,6],[3,7]  // sides
    ];
    
    edges.forEach(([a, b]) => {
      const points = [
        new THREE.Vector3(...vertices[a]),
        new THREE.Vector3(...vertices[b])
      ];
      const geometry = new THREE.BufferGeometry().setFromPoints(points);
      const line = new THREE.Line(geometry, mat);
      group.add(line);
    });
  };
  
  drawCube(innerVertices, material);
  drawCube(outerVertices, material);
  
  // Connect inner to outer
  for (let i = 0; i < 8; i++) {
    const points = [
      new THREE.Vector3(...innerVertices[i]),
      new THREE.Vector3(...outerVertices[i])
    ];
    const geometry = new THREE.BufferGeometry().setFromPoints(points);
    const line = new THREE.Line(geometry, material);
    group.add(line);
  }
  
  return group;
};

/**
 * Create MASSIVE spiral helix - DNA-like structure
 */
export const createSpiralHelix = (radius = 200, height = 600, turns = 8, points = 200) => {
  const curve = new THREE.CurvePath();
  
  const helix = new THREE.CatmullRomCurve3(
    Array.from({ length: points }, (_, i) => {
      const t = i / points;
      const angle = t * turns * Math.PI * 2;
      return new THREE.Vector3(
        Math.cos(angle) * radius,
        Math.sin(angle) * radius,
        t * height - height / 2
      );
    })
  );
  
  const tubeGeometry = new THREE.TubeGeometry(helix, points, 8, 16, false);
  const material = new THREE.MeshPhongMaterial({
    color: 0xff8c00, // AMBER
    emissive: 0xff8c00,
    emissiveIntensity: 0.5,
    transparent: true,
    opacity: 0.7
  });
  
  const mesh = new THREE.Mesh(tubeGeometry, material);
  return mesh;
};

/**
 * Animate geometry - rotation and pulsing
 */
export const animateGeometry = (geometry, axis = 'y', speed = 0.001) => {
  const animate = () => {
    if (axis === 'x') geometry.rotation.x += speed;
    if (axis === 'y') geometry.rotation.y += speed;
    if (axis === 'z') geometry.rotation.z += speed;
    
    requestAnimationFrame(animate);
  };
  
  animate();
};